/**
 * Horizontal Scroll Handlers for Native Interactive Site Map (ISM)
 *
 * Pure DOM event delegation — no Redux, no CSS, no business logic. Reports horizontal scrolling
 * on a given element (the map chips bar, the listing card grid, or any other
 * horizontally-scrollable element this feature adds later), debounced so one scroll gesture fires
 * one callback instead of one per scroll tick. Any device/breakpoint gating is the caller's
 * responsibility (see plugins/tracking.plugin.js) — this module doesn't know about breakpoints.
 *
 * Listens in the capture phase so a caller can bind once on a stable ancestor (e.g. the listing
 * panel) and still hear `scroll` from a descendant scrollable element that gets replaced via
 * `innerHTML` on every render (e.g. the card grids) — plain bubble-phase listening never sees
 * these, since `scroll` doesn't bubble. Capture works the same as bubble when `element` is itself
 * the scrolling target, so this is safe for both use cases.
 *
 * @module nativeInteractiveSiteMap/handlers/scrollHandlers
 */
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const SCROLL_DEBOUNCE_MS = 300;

function createScrollHandlers() {
  const state = {
    subscription: null,
  };

  /**
   * @param {HTMLElement} element
   * @param {() => void} onScrolled
   */
  function bind(element, onScrolled) {
    state.subscription?.unsubscribe();
    state.subscription = fromEvent(element, 'scroll', { passive: true, capture: true })
      .pipe(debounceTime(SCROLL_DEBOUNCE_MS))
      .subscribe(onScrolled);
  }

  function unbind() {
    state.subscription?.unsubscribe();
    state.subscription = null;
  }

  return { bind, unbind };
}

export default createScrollHandlers;
